import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logo from './logo.svg';
import './TraineeLanding.css';

const API_BASE = process.env.REACT_APP_TRAINEE_API_BASE || 'https://api.pacificpearltraining.site';
const STORAGE_KEY = 'vasisTraineeLandingSession';

const readStoredSession = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

const saveStoredSession = (session) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
};

const clearStoredSession = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const formatDateTime = (value) => {
  if (!value) return 'Not started';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not started';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const percent = (completed, target) => {
  if (!target) return 0;
  return Math.max(0, Math.min(100, Math.round((completed / target) * 100)));
};

function TraineeLanding() {
  const storedSession = useMemo(readStoredSession, []);
  const [email, setEmail] = useState(storedSession?.email || '');
  const [restaurantTag, setRestaurantTag] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [landingData, setLandingData] = useState(null);
  const [formError, setFormError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [videoReadyToComplete, setVideoReadyToComplete] = useState(false);
  const [maxWatchedSeconds, setMaxWatchedSeconds] = useState(0);
  const videoRef = useRef(null);

  const progress = landingData?.progress || null;
  const customer = landingData?.customer || null;
  const config = landingData?.config || {};
  const targetOrders = progress?.helperOrders?.target || config.targetOrders || 50;
  const completedOrders = progress?.helperOrders?.completed || 0;
  const orderProgressPercent = percent(completedOrders, targetOrders);
  const videoComplete = progress?.steps?.video?.status === 'completed';
  const firstOrderComplete = progress?.steps?.pos?.status === 'completed';
  const trainingComplete = completedOrders >= targetOrders;
  const currentSession = landingData
    ? { email: landingData.customer.email, accessCode: landingData.progress.accessCode }
    : storedSession;

  const completedTasks = [
    Boolean(progress),
    videoComplete,
    firstOrderComplete,
    trainingComplete,
  ].filter(Boolean).length;

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Request failed.');
    }
    return payload;
  }, []);

  const refreshProgress = useCallback(async ({ silent = false } = {}) => {
    const session = readStoredSession();
    if (!session?.email) return null;

    if (!silent) {
      setIsRefreshing(true);
      setStatusMessage('');
    }

    try {
      const params = new URLSearchParams({
        email: session.email,
        accessCode: session.accessCode || '',
      });
      const payload = await fetchJson(`${API_BASE}/api/trainee-landing/progress?${params.toString()}`);
      setLandingData(payload);
      saveStoredSession({
        email: payload.customer.email,
        accessCode: payload.progress.accessCode,
      });
      return payload;
    } catch (error) {
      if (!silent) setFormError(error.message);
      return null;
    } finally {
      if (!silent) setIsRefreshing(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    let mounted = true;

    fetchJson(`${API_BASE}/api/trainee-landing/restaurants`)
      .then((payload) => {
        if (!mounted) return;
        setRestaurants(payload.restaurants || []);
        const params = new URLSearchParams(window.location.search);
        const restaurantParam = params.get('restaurant') || params.get('restaurantTag');
        const matchingRestaurant = (payload.restaurants || []).find((restaurant) => (
          restaurant.tag.toLowerCase() === String(restaurantParam || '').toLowerCase()
          || restaurant.label.toLowerCase().includes(String(restaurantParam || '').toLowerCase())
        ));
        if (matchingRestaurant) {
          setRestaurantTag(matchingRestaurant.tag);
        } else if ((payload.restaurants || []).length === 1) {
          setRestaurantTag(payload.restaurants[0].tag);
        }
      })
      .catch((error) => setFormError(error.message));

    return () => {
      mounted = false;
    };
  }, [fetchJson]);

  useEffect(() => {
    if (storedSession?.email) {
      refreshProgress({ silent: true });
    }
  }, [refreshProgress, storedSession?.email]);

  useEffect(() => {
    if (!landingData) return undefined;
    const intervalId = window.setInterval(() => {
      refreshProgress({ silent: true });
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [landingData, refreshProgress]);

  const startAccount = async (event) => {
    event.preventDefault();
    setFormError('');
    setStatusMessage('');

    if (!email.trim()) {
      setFormError('Enter the email address you want to use for training.');
      return;
    }
    if (!restaurantTag) {
      setFormError('Choose the restaurant training set for this account.');
      return;
    }

    setIsStarting(true);
    try {
      const payload = await fetchJson(`${API_BASE}/api/trainee-landing/start`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          restaurantTag,
        }),
      });

      setLandingData(payload);
      saveStoredSession({
        email: payload.customer.email,
        accessCode: payload.progress.accessCode,
      });
      setEmail(payload.customer.email);
      setRestaurantTag(payload.progress.restaurantTag || restaurantTag);
      setStatusMessage(payload.created
        ? 'Training ID created. Use the Shopify email invite when you open the POS.'
        : 'Training ID loaded. Your progress is synced from Shopify.');
    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsStarting(false);
    }
  };

  const recordEvent = async (eventType, payload = {}) => {
    if (!currentSession?.email) return null;

    const result = await fetchJson(`${API_BASE}/api/trainee-landing/events`, {
      method: 'POST',
      body: JSON.stringify({
        email: currentSession.email,
        accessCode: currentSession.accessCode,
        eventType,
        payload,
      }),
    });
    setLandingData(result);
    saveStoredSession({
      email: result.customer.email,
      accessCode: result.progress.accessCode,
    });
    return result;
  };

  const completeVideo = async () => {
    const video = videoRef.current;
    const durationSeconds = video?.duration && Number.isFinite(video.duration) ? video.duration : 0;
    const watchedSeconds = Math.max(maxWatchedSeconds, durationSeconds);

    try {
      setFormError('');
      await recordEvent('video_completed', {
        watchedSeconds,
        durationSeconds,
      });
      setStatusMessage('Video complete. The POS task is unlocked.');
    } catch (error) {
      setFormError(error.message);
    }
  };

  const openPos = async () => {
    try {
      setFormError('');
      await recordEvent('pos_opened');
    } catch (error) {
      setFormError(error.message);
    }
    window.open(config.posUrl || 'https://order-taking-2026-66217e6b27756711050e.o2.myshopify.dev/agent-dashboard', '_blank', 'noopener,noreferrer');
  };

  const openOrderHelper = async () => {
    if (!customer?.id) return;

    try {
      setFormError('');
      await recordEvent('helper_opened');
      const payload = await fetchJson(`${API_BASE}/login`, {
        method: 'POST',
        body: JSON.stringify({
          shopifyId: customer.id,
          tags: customer.tags || [],
        }),
      });
      if (payload.url) {
        window.open(payload.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      setFormError(error.message);
    }
  };

  const signOutLanding = () => {
    clearStoredSession();
    setLandingData(null);
    setStatusMessage('');
    setFormError('');
    setVideoReadyToComplete(false);
    setMaxWatchedSeconds(0);
  };

  const onVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextWatched = Math.max(maxWatchedSeconds, video.currentTime || 0);
    setMaxWatchedSeconds(nextWatched);
    if (video.duration && Number.isFinite(video.duration)) {
      setVideoReadyToComplete(nextWatched >= video.duration * 0.96);
    }
  };

  const taskRows = [
    {
      number: '01',
      title: 'Create Training ID',
      status: progress ? 'complete' : 'active',
      detail: progress
        ? `${customer?.email || email} is connected to ${progress.restaurantTag || 'training'}.`
        : 'Enter your email so the system can create your trainee account.',
    },
    {
      number: '02',
      title: 'Watch POS Walkthrough',
      status: videoComplete ? 'complete' : progress ? 'active' : 'locked',
      detail: videoComplete
        ? `Completed ${formatDateTime(progress?.steps?.video?.completedAt)}.`
        : 'Watch the full walkthrough before opening the POS task.',
    },
    {
      number: '03',
      title: 'Create First POS Order',
      status: firstOrderComplete ? 'complete' : videoComplete ? 'active' : 'locked',
      detail: firstOrderComplete
        ? `First order detected ${formatDateTime(progress?.steps?.pos?.firstOrderCompletedAt)}.`
        : 'Open the POS, log in with the trainee Shopify account, and submit one order.',
    },
    {
      number: '04',
      title: 'Complete 50 Helper Orders',
      status: trainingComplete ? 'complete' : firstOrderComplete ? 'active' : 'locked',
      detail: `${completedOrders} of ${targetOrders} scored helper orders completed.`,
    },
  ];

  return (
    <main className="trainee-page">
      <section className="trainee-shell">
        <div className="trainee-brand-row">
          <img src={logo} alt="Vasis" className="trainee-logo" />
          <div>
            <p className="trainee-kicker">Vasis hiring training</p>
            <h1>Order-taking readiness</h1>
          </div>
          {landingData && (
            <button type="button" className="text-button" onClick={signOutLanding}>
              Change email
            </button>
          )}
        </div>

        <div className="trainee-intro">
          <div>
            <h2>Learn the POS in measured steps.</h2>
            <p>
              We train restaurant order-taking agents to move accurately through live menu workflows.
              This first exercise measures how quickly you learn the POS, how carefully you handle
              special requests, and how your accuracy improves over repeated practice.
            </p>
          </div>
          <div className="trainee-progress-summary">
            <span>{completedTasks}/4 tasks</span>
            <div className="summary-track" aria-hidden="true">
              <div style={{ width: `${percent(completedTasks, 4)}%` }} />
            </div>
          </div>
        </div>

        {!landingData && (
          <form className="trainee-entry-panel" onSubmit={startAccount}>
            <div>
              <label htmlFor="trainee-email">Email</label>
              <input
                id="trainee-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="trainee-restaurant">Training restaurant</label>
              <select
                id="trainee-restaurant"
                value={restaurantTag}
                onChange={(event) => setRestaurantTag(event.target.value)}
              >
                <option value="">Choose one</option>
                {restaurants.map((restaurant) => (
                  <option key={restaurant.tag} value={restaurant.tag}>
                    {restaurant.label}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" disabled={isStarting}>
              {isStarting ? 'Creating ID...' : 'Start Training'}
            </button>
          </form>
        )}

        {(formError || statusMessage) && (
          <div className={formError ? 'trainee-alert error' : 'trainee-alert'}>
            {formError || statusMessage}
          </div>
        )}

        {landingData && (
          <>
            <section className="video-workspace" aria-labelledby="training-video-title">
              <div className="video-copy">
                <p className="section-label">Task 02</p>
                <h2 id="training-video-title">Watch the POS walkthrough</h2>
                <p>
                  The video shows the basic order-taking motion before you enter the POS.
                  The next task unlocks after the video reaches the end.
                </p>
              </div>
              <div className="video-frame">
                {config.videoUrl ? (
                  <video
                    ref={videoRef}
                    controls
                    playsInline
                    preload="metadata"
                    src={config.videoUrl}
                    onTimeUpdate={onVideoTimeUpdate}
                    onEnded={() => setVideoReadyToComplete(true)}
                  />
                ) : (
                  <div className="video-placeholder">
                    <span>Training video pending</span>
                    <p>The walkthrough will appear here as soon as the training video is ready.</p>
                  </div>
                )}
              </div>
              <div className="video-actions">
                <button
                  type="button"
                  onClick={completeVideo}
                  disabled={videoComplete || !config.videoUrl || !videoReadyToComplete}
                >
                  {videoComplete ? 'Video Complete' : 'Complete Video Task'}
                </button>
              </div>
            </section>

            <section className="task-grid" aria-label="Training tasks">
              {taskRows.map((task) => (
                <article className={`task-tile ${task.status}`} key={task.number}>
                  <div className="task-number">{task.number}</div>
                  <div>
                    <h3>{task.title}</h3>
                    <p>{task.detail}</p>
                  </div>
                  <span className="task-status">{task.status}</span>
                </article>
              ))}
            </section>

            <section className="practice-console">
              <div className="console-main">
                <p className="section-label">Practice run</p>
                <h2>{completedOrders} / {targetOrders} scored orders</h2>
                <div className="order-track" aria-label={`${orderProgressPercent}% complete`}>
                  <div style={{ width: `${orderProgressPercent}%` }} />
                </div>
                <div className="metric-row">
                  <div>
                    <span>Average score</span>
                    <strong>{progress?.stats?.averageScore ?? 'N/A'}</strong>
                  </div>
                  <div>
                    <span>Latest score</span>
                    <strong>{progress?.stats?.latestScore ?? 'N/A'}</strong>
                  </div>
                  <div>
                    <span>Best score</span>
                    <strong>{progress?.stats?.bestScore ?? 'N/A'}</strong>
                  </div>
                </div>
              </div>
              <div className="console-actions">
                <button type="button" onClick={openPos} disabled={!videoComplete}>
                  Open POS
                </button>
                <button type="button" onClick={openOrderHelper} disabled={!firstOrderComplete}>
                  Open Order Helper
                </button>
                <button type="button" className="secondary-action" onClick={() => refreshProgress()} disabled={isRefreshing}>
                  {isRefreshing ? 'Refreshing...' : 'Refresh Progress'}
                </button>
              </div>
            </section>

            <section className="recent-orders">
              <div>
                <p className="section-label">Latest scored orders</p>
                <h2>Progress history</h2>
              </div>
              <div className="order-list">
                {(progress?.orders || []).slice(0, 8).map((order) => (
                  <div className="order-row" key={order.traineeOrderId || order.traineeOrderName}>
                    <span>{order.traineeOrderName || `Order ${order.traineeOrderId}`}</span>
                    <span>{formatDateTime(order.traineeOrderCreatedAt)}</span>
                    <strong>{order.score ?? 'N/A'}</strong>
                  </div>
                ))}
                {(progress?.orders || []).length === 0 && (
                  <div className="order-empty">
                    Scored helper orders will appear here after the first comparison.
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </section>
    </main>
  );
}

export default TraineeLanding;
